"use client";

import { cn } from "@/lib/utils";
import Image from "next/image";
import React, { useState } from "react";

interface SpecialisationMetadata {
  id: string;
  card_title: string;
  card_description: string;
  detail_title?: string;
  detail_subtitle?: string;
  detail_description?: string;
  icon?: string;
  icon_alt?: string;
  hover_icon?: string;
  hover_icon_alt?: string;
  priority?: number;
  meta_status?: boolean;
  // Desktop styling
  desktop_title_text_color?: string;
  desktop_active_card_color?: string;
  desktop_inactive_card_color?: string;
  desktop_description_gradient?: string;
  desktop_sub_title_text_color?: string;
  desktop_description_text_color?: string;
  desktop_active_title_text_color?: string;
  desktop_active_sub_title_text_color?: string;
  // Desktop gradient fields
  desktop_gradient_angle?: string;
  desktop_gradient_start_color?: string;
  desktop_gradient_end_color?: string;
}

interface CourseSpecialisationsDesktopProps {
  title: string;
  titleTextColor: string;
  backgroundColor: string;
  displayData: SpecialisationMetadata[];
  onCardClick?: (index: number, item: SpecialisationMetadata) => void;
}

// Default icon component
const DefaultIcon = ({ isSelected }: { isSelected: boolean }) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M12 2L2 7L12 12L22 7L12 2Z"
      stroke={isSelected ? "#0A3D5B" : "#F8F9FA"}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M2 17L12 22L22 17"
      stroke={isSelected ? "#0A3D5B" : "#F8F9FA"}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M2 12L12 17L22 12"
      stroke={isSelected ? "#0A3D5B" : "#F8F9FA"}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// Parse gradient string to extract colors
function parseGradient(gradientStr?: string): {
  from: string;
  via: string;
  to: string;
} {
  // Default gradient
  const defaultGradient = {
    from: "#082F47",
    via: "#0E5479",
    to: "#2375A8",
  };

  if (!gradientStr) return defaultGradient;

  // Try to extract colors from gradient string
  // Format: "linear-gradient(180deg, #082F47 0%, #0E5479 50%, #2375A8 100%)"
  const colorMatches = gradientStr.match(/#[0-9A-Fa-f]{6}/g);
  if (colorMatches && colorMatches.length >= 3) {
    return {
      from: colorMatches[0],
      via: colorMatches[1],
      to: colorMatches[2],
    };
  }

  return defaultGradient;
}

const CourseSpecialisationsDesktop = React.forwardRef<
  HTMLElement,
  CourseSpecialisationsDesktopProps
>(
  (
    { title, titleTextColor, backgroundColor, displayData, onCardClick },
    ref
  ) => {
    const [selectedIndex, setSelectedIndex] = useState<number>(0);
    const selectedItem = displayData[selectedIndex] || null;

    // Split data into rows of 3
    const rows = [];
    for (let i = 0; i < displayData.length; i += 3) {
      rows.push(displayData.slice(i, i + 3));
    }

    // Find which row the selected item is in
    const selectedRowIndex = Math.floor(selectedIndex / 3);

    // Get gradient colors and angle from selected item
    // Prioritize individual gradient fields if available
    const gradientAngle = selectedItem?.desktop_gradient_angle || "180deg";
    const gradient =
      selectedItem?.desktop_gradient_start_color &&
      selectedItem?.desktop_gradient_end_color
        ? {
            from: selectedItem.desktop_gradient_start_color,
            via: selectedItem.desktop_gradient_end_color, // Use end color as via for 2-color gradient
            to: selectedItem.desktop_gradient_end_color,
          }
        : parseGradient(selectedItem?.desktop_description_gradient);

    return (
      <section
        ref={ref}
        className={cn("w-full flex items-center justify-center px-9 py-11")}
        style={{ backgroundColor }}
      >
        <div
          className={cn(
            "flex flex-col items-start justify-center w-full gap-8",
            "sm:w-[1008px]",
            "md:w-[1158px]",
            "lg:w-[1358px]",
            "xl:w-[1678px]"
          )}
        >
          <p
            className={cn(
              "text-heading-6-semibold",
              "sm:text-heading-5-semibold",
              "md:text-heading-4-semibold",
              "lg:text-new-heading-34-semibold",
              "xl:text-new-heading-42-semibold"
            )}
            style={{ color: titleTextColor }}
          >
            {title}
          </p>

          <div className="flex flex-col w-full">
            {rows.map((row, rowIndex) => (
              <div key={rowIndex} className="flex flex-col w-full">
                {/* Row of Cards */}
                <div className="flex gap-3 w-full py-4">
                  {row.map((item, colIndex) => {
                    const globalIndex = rowIndex * 3 + colIndex;
                    const isSelected = selectedIndex === globalIndex;

                    // Per-item colors
                    const activeCardColor =
                      item.desktop_active_card_color || "#0A3D5B";
                    const inactiveCardColor =
                      item.desktop_inactive_card_color || "#E4EEF6";
                    const titleColor =
                      item.desktop_title_text_color || "#101112";
                    const subTitleColor =
                      item.desktop_sub_title_text_color || "#101112";
                    const activeTitleColor =
                      item.desktop_active_title_text_color || "#F8F9FA";
                    const activeSubTitleColor =
                      item.desktop_active_sub_title_text_color || "#F8F9FA";

                    return (
                      <div
                        key={item.id}
                        className={cn(
                          "flex-1 flex gap-3 items-start p-4",
                          "rounded-[10px] cursor-pointer transition-all duration-300",
                          "shadow-[0px_2px_4px_0px_rgba(0,0,0,0.1)]"
                        )}
                        style={{
                          backgroundColor: isSelected
                            ? activeCardColor
                            : inactiveCardColor,
                        }}
                        onClick={() => {
                          setSelectedIndex(globalIndex);
                          onCardClick?.(globalIndex, item);
                        }}
                      >
                        {/* Icon Container */}
                        <div
                          className={cn(
                            "flex items-center justify-center",
                            "w-8 h-8 md:w-9 md:h-9 lg:w-10 lg:h-10 xl:w-12 xl:h-12 rounded-full flex-shrink-0",
                            "shadow-[0px_1px_3px_0px_rgba(0,0,0,0.1),0px_1px_2px_-1px_rgba(0,0,0,0.1)]"
                          )}
                          style={{
                            background: isSelected
                              ? "linear-gradient(to bottom, #ffffff, #f0f4f7)"
                              : "linear-gradient(to bottom, #0a3d5b, #0d5176)",
                          }}
                        >
                          {item.icon ? (
                            <div className="relative w-8 h-8 md:w-9 md:h-9 lg:w-10 lg:h-10 xl:w-12 xl:h-12 rounded-full overflow-hidden">
                              <Image
                                src={
                                  isSelected && item?.hover_icon
                                    ? item?.hover_icon
                                    : item.icon
                                }
                                alt={
                                  isSelected && item.hover_icon_alt
                                    ? item.hover_icon_alt
                                    : item.icon_alt || item.card_title
                                }
                                fill
                                className="object-contain"
                              />
                            </div>
                          ) : (
                            <DefaultIcon isSelected={isSelected} />
                          )}
                        </div>

                        {/* Card Content */}
                        <div className="flex flex-col gap-1 flex-1">
                          <p
                            className={cn(
                              "text-heading-7-semibold",
                              "sm:text-heading-7-semibold",
                              "md:text-body-5-semibold",
                              "lg:text-heading-6-semibold",
                              "xl:text-heading-3-semibold",
                              "transition-colors duration-300"
                            )}
                            style={{
                              color: isSelected ? activeTitleColor : titleColor,
                            }}
                          >
                            {item.card_title}
                          </p>
                          <p
                            className={cn(
                              "text-heading-9-regular",
                              "sm:text-heading-9-regular",
                              "md:text-body-2-regular",
                              "lg:text-body-1-regular",
                              "xl:text-heading-6-regular",
                              "transition-colors duration-300"
                            )}
                            style={{
                              color: isSelected
                                ? activeSubTitleColor
                                : subTitleColor,
                            }}
                          >
                            {item.card_description}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                  {/* Fill empty slots in the last row */}
                  {row.length < 3 &&
                    Array.from({ length: 3 - row.length }).map((_, i) => (
                      <div key={`empty-${i}`} className="flex-1" />
                    ))}
                </div>

                {/* Detail Panel - shown after the row containing the selected item */}
                {selectedRowIndex === rowIndex && selectedItem && (
                  <div
                    className={cn(
                      "w-full rounded-[12px] px-6 py-4",
                      "transition-all duration-300"
                    )}
                    style={{
                      background: `linear-gradient(${gradientAngle}, ${gradient.from}, ${gradient.via} 50%, ${gradient.to})`,
                    }}
                  >
                    <div className="flex flex-col gap-3 w-full">
                      <div className="flex gap-2 items-center">
                        <p
                          className={cn(
                            "text-heading-6-semibold",
                            "sm:text-heading-6-semibold",
                            "md:text-heading-3-semibold",
                            "lg:text-new-heading-36-semibold",
                            "xl:text-heading-2-semibold"
                          )}
                          style={{
                            color:
                              selectedItem.desktop_active_title_text_color ||
                              "#F8F9FA",
                          }}
                        >
                          {selectedItem.detail_title || selectedItem.card_title}
                        </p>
                        <div className="flex items-center pt-[7px]">
                          <p
                            className={cn(
                              "text-heading-9-semibold",
                              "sm:text-heading-9-semibold",
                              "md:text-label-5-semibold",
                              "lg:text-heading-7-semibold",
                              "xl:text-heading-6-semibold"
                            )}
                            style={{
                              color:
                                selectedItem.desktop_active_sub_title_text_color ||
                                "#F8F9FA",
                            }}
                          >
                            {selectedItem.detail_subtitle ||
                              selectedItem.card_description}
                          </p>
                        </div>
                      </div>

                      <p
                        className={cn(
                          "text-body-4-regular",
                          "md:text-body-2-regular"
                        )}
                        style={{
                          color:
                            selectedItem.desktop_description_text_color ||
                            "#F8F9FA",
                        }}
                      >
                        {selectedItem.detail_description ||
                          selectedItem.card_description}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }
);

export default CourseSpecialisationsDesktop;
